import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useNavigate,
  redirect,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { isAdminLoggedIn, logoutAdmin } from "@/lib/admin-login-api";
import {
  LayoutDashboard,
  Building2,
  Users,
  Bell,
  Calendar,
  Settings,
  Sparkles,
  LogOut,
  Bike,
  UserCheck,
  Award,
  Menu,
  X,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/admin")({
  // Guard every /admin/* route except the login screen itself.
  beforeLoad: ({ location }) => {
    const path = location.pathname.replace(/\/+$/, "");
    const onLogin = path.endsWith("/admin/login");
    if (!onLogin && !isAdminLoggedIn()) {
      throw redirect({ to: "/admin/login" });
    }
  },
  head: () => ({ meta: [{ title: "Admin Portal — Jain Gaurav Yatra" }] }),
  component: AdminLayout,
});

function AdminLayout() {
  const loc = useLocation();
  const nav = useNavigate();
  const { t } = useI18n();
  const [loggingOut, setLoggingOut] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!sidebarOpen) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sidebarOpen]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [loc.pathname]);

  const handleLogout = async () => {
    if (loggingOut) {
      return;
    }
    setLoggingOut(true);
    try {
      await logoutAdmin();
      toast.success("Signed out.");
    } catch {
      toast.error("Sign-out failed, but your local session was cleared.");
    } finally {
      nav({ to: "/admin/login" });
    }
  };

  const pathname = (loc.pathname || "/").replace(/\/$/, "") || "/";
  const isAdminLogin = pathname === "/admin/login" || pathname.endsWith("/admin/login");
  if (isAdminLogin) {
    return <Outlet />;
  }

  const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
    { to: "/admin/temples", label: "Temples", icon: Building2 },
    { to: "/admin/volunteers", label: "Volunteers", icon: UserCheck },
    { to: "/admin/rickshaws", label: "Rickshaws", icon: Bike },
    { to: "/admin/members", label: "Members", icon: Users },
    { to: "/admin/announcements", label: "Announcements", icon: Bell },
    { to: "/admin/bookings", label: "Bookings", icon: Calendar },
    { to: "/admin/certificates", label: "Certificates", icon: Award },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="flex min-h-screen" style={{ background: "hsl(var(--cream-deep))" }}>
      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          aria-label="Close sidebar"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-screen w-64 shrink-0 flex-col border-r border-border bg-card transition-transform duration-200 md:sticky md:top-0 md:z-auto md:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-5">
          <Link
            to="/"
            className="flex min-w-0 items-center gap-2"
            onClick={() => setSidebarOpen(false)}
          >
            <span className="icon-tile h-9 w-9 shrink-0">
              <Sparkles className="h-4 w-4" />
            </span>
            <div>
              <div className="font-serif text-base font-bold">Jain Gaurav</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("admin")}
              </div>
            </div>
          </Link>
          <button
            type="button"
            className="rounded-full bg-muted p-2 text-muted-foreground hover:bg-secondary hover:text-foreground md:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV.map((n) => {
            const active = n.exact ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${active ? "text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                style={active ? { background: "var(--gradient-saffron)" } : undefined}
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <button
          type="button"
          onClick={handleLogout}
          disabled={loggingOut}
          className="m-3 flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm text-muted-foreground transition hover:text-foreground disabled:opacity-60"
        >
          <LogOut className="h-4 w-4" /> {loggingOut ? "Signing out…" : "Exit Admin"}
        </button>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border bg-card/80 px-4 backdrop-blur sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              aria-expanded={sidebarOpen}
              aria-label="Open sidebar"
              className="inline-flex shrink-0 rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">{t("welcome")}</div>
              <div className="truncate font-serif text-lg font-bold">{t("admin")}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-full font-bold text-primary-foreground"
              style={{ background: "var(--gradient-saffron)" }}
            >
              A
            </div>
          </div>
        </header>
        <div className="md:p-6 p-4">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
